<script setup>
// [PROSPEK] 2026-04-05 Add computed, watch, timeStampAppendedURL for delayed attachment reactivity
import { computed, ref, watch } from 'vue';
// [/PROSPEK]
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';
// [PROSPEK] 2026-04-05 Cache-bust delayed attachment URLs
import { timeStampAppendedURL } from 'dashboard/helper/URLHelper';
// [/PROSPEK]

// [PROSPEK] 2026-04-05 Capture props for reactive watch, add error handling
const props = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
});

const hasError = ref(false);
// [/PROSPEK]
const showGallery = ref(false);

const { filteredCurrentChatAttachments } = useMessageContext();

// [PROSPEK] 2026-04-05 Cache-busted URL for delayed attachment reactivity
const mediaUrl = computed(() => {
  return props.attachment?.dataUrl
    ? timeStampAppendedURL(props.attachment.dataUrl)
    : '';
});
// [/PROSPEK]

// [PROSPEK] 2026-04-05 Reset video thumbnail error state when delayed attachment updates arrive
watch(
  () => props.attachment,
  () => {
    hasError.value = false;
  },
  { immediate: true }
);
// [/PROSPEK]

// [PROSPEK] 2026-04-05 Error handler for video load failures
const handleError = () => {
  hasError.value = true;
};
// [/PROSPEK]
</script>

<template>
  <div
    class="size-[72px] overflow-hidden contain-content rounded-xl cursor-pointer relative group"
    @click="showGallery = true"
  >
    <!-- [PROSPEK] 2026-04-05 Error state for failed video thumbnails -->
    <div
      v-if="hasError"
      class="flex flex-col items-center justify-center gap-1 text-xs text-center rounded-lg size-full bg-n-alpha-1 text-n-slate-11"
    >
      <Icon icon="i-lucide-circle-off" class="text-n-slate-11" />
      {{ $t('COMPONENTS.MEDIA.LOADING_FAILED') }}
    </div>
    <!-- [/PROSPEK] -->
    <video
      v-else
      :src="mediaUrl"
      class="w-full h-full object-cover"
      muted
      playsInline
      @error="handleError"
    />
    <div
      class="absolute w-full h-full inset-0 p-1 flex items-center justify-center"
    >
      <div
        class="size-7 bg-n-slate-1/60 backdrop-blur-sm rounded-full overflow-hidden shadow-[0_5px_15px_rgba(0,0,0,0.4)]"
      >
        <Icon
          icon="i-teenyicons-play-small-solid"
          class="size-7 text-n-slate-12/80 backdrop-blur"
        />
      </div>
    </div>
  </div>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    @error="handleError"
    @close="() => (showGallery = false)"
  />
</template>
